import React from 'react';

export interface Chip<T> {
  id: T;
  label: string;
  title?: string;
}

interface ChipGroupProps<T> {
  chips: Chip<T>[];
  value: T;
  onChange(id: T): void;
  ariaLabel?: string;
  className?: string;
}

/**
 * A segmented control: range pickers, unit toggles, thresholds.
 *
 * The selected chip is raised onto the panel colour with the small offset
 * shadow rather than being tinted, so the group reads as one physical switch
 * with a pressed segment instead of a row of independent buttons.
 *
 * `aria-pressed` carries the state, so selection is not communicated by
 * contrast alone.
 */
export const ChipGroup = <T extends string | number>(props: ChipGroupProps<T>) => (
  <div
    role="group"
    aria-label={props.ariaLabel}
    className={`chip-group flex items-center gap-1 rounded-md border border-soft bg-page-alt p-0.5${props.className ? ` ${props.className}` : ''}`}
  >
    {props.chips.map((chip) => (
      <button
        key={String(chip.id)}
        type="button"
        title={chip.title}
        aria-pressed={chip.id === props.value}
        onClick={() => props.onChange(chip.id)}
        className={`h-7 cursor-pointer rounded border-none px-2.5 text-[12px] font-semibold ${
          chip.id === props.value
            ? 'bg-panel text-fg-1 shadow-chunky-sm'
            : 'bg-transparent text-fg-3 hover:text-fg-1'
        }`}
      >
        {chip.label}
      </button>
    ))}
  </div>
);
